import React from 'react';

// Light markdown for AI Chad replies: **bold** segments + [[step:...]] links.
// Deliberately NOT a full markdown renderer — keep Chad's text feeling like
// chat, not a document. Rendered inside whitespace-pre-wrap containers, so
// newlines and "- " bullets come through as written.
//
// STEP TOKENS — Chad wraps journey-step mentions as [[step:STEP_ID|Step Name]]
// (see STEP LINKING in backend SYSTEM_PROMPT). Tokens are ALWAYS consumed and
// NEVER render raw:
//   • opts.onStepHover/onStepClick provided → quiet inline link (subtle
//     underline + tint, not button-like) that the host wires to the journey
//     strip (hover-highlight / click-open).
//   • no opts (e.g. the AiChad floater, anywhere without a strip) → the step
//     name renders as clean plain text.
// Tokens are processed INSIDE bold segments too, so a token can never leak
// regardless of where the model places it.

const STEP_TOKEN_SPLIT = /(\[\[step:[^\]]*\]\])/g;
const STEP_TOKEN = /^\[\[step:([^|\]]+)(?:\|([^\]]*))?\]\]$/;

// MD-2 — GO TOKENS. Chad wraps a destination as [[go:PATH|Page name]] (see
// NAVIGATION in the backend prompt). The app, not Chad, does the navigating:
// the token becomes a link that raises 'keymade:chad-go' for the one listener
// mounted inside the Router. Only in-app paths are linked; anything else
// degrades to its label, and the token never renders raw.
const GO_TOKEN_SPLIT = /(\[\[go:[^\]]*\]\])/g;
const GO_TOKEN = /^\[\[go:([^|\]]+)(?:\|([^\]]*))?\]\]$/;
const LINK_CLASS = 'cursor-pointer underline decoration-dotted decoration-[#BC5D41]/60 underline-offset-2 text-[#BC5D41] hover:text-[#70170F] transition-colors';
const go = (path) => window.dispatchEvent(new CustomEvent('keymade:chad-go', { detail: { path } }));

function renderGoTokens(text, keyPrefix) {
  if (!text || !text.includes('[[go:')) return numerate(text, keyPrefix);
  return text.split(GO_TOKEN_SPLIT).map((seg, j) => {
    const m = seg.match(GO_TOKEN);
    if (!m) return numerate(seg, `${keyPrefix}-${j}`);
    const path = m[1].trim();
    const label = (m[2] || '').trim() || path;
    // AC-3 — a chain's page reads /projects/{id}/… ; an unfilled placeholder
    // would navigate nowhere, so it reads as plain words instead.
    if (!path.startsWith('/') || path.includes('://') || path.includes('{')) return numerate(label, `${keyPrefix}-${j}`);
    return (
      <span
        key={`${keyPrefix}-go-${j}`}
        role="link"
        tabIndex={0}
        className={LINK_CLASS}
        data-testid="chad-go-link"
        data-path={path}
        onClick={() => go(path)}
        onKeyDown={(e) => { if (e.key === 'Enter') go(path); }}
      >
        {numerate(label, `${keyPrefix}-${j}`)}
      </span>
    );
  });
}

// DP24 — Chad speaks in the display face, and the display face never sets a
// digit. Numeric runs are lifted into the technical face on the way out, so
// "30 active projects" reads with Plex Mono figures inside a Bodoni sentence.
const NUMERAL_RUN = /(\d[\d.,:/%°$-]*)/g;

function numerate(text, keyPrefix) {
  if (typeof text !== 'string' || !/\d/.test(text)) return text;
  return text.split(NUMERAL_RUN).map((seg, k) => (
    /^\d/.test(seg)
      ? <span key={`${keyPrefix}-n-${k}`} className="kmd-num">{seg}</span>
      : seg
  ));
}

function renderStepTokens(text, opts, keyPrefix) {
  if (!text || !text.includes('[[step:')) return renderGoTokens(text, keyPrefix);
  const { onStepHover, onStepClick } = opts;
  return text.split(STEP_TOKEN_SPLIT).map((seg, j) => {
    const m = seg.match(STEP_TOKEN);
    if (!m) return renderGoTokens(seg, `${keyPrefix}-${j}`);
    const stepId = m[1].trim();
    const label = (m[2] || '').trim() || stepId;
    if (!onStepClick && !onStepHover) return numerate(label, `${keyPrefix}-${j}`); // plain-text degrade
    return (
      <span
        key={`${keyPrefix}-step-${j}`}
        role="link"
        tabIndex={0}
        className={LINK_CLASS}
        data-testid={`chad-step-link-${stepId}`}
        onMouseEnter={() => onStepHover && onStepHover(stepId, true)}
        onMouseLeave={() => onStepHover && onStepHover(stepId, false)}
        onClick={() => onStepClick && onStepClick(stepId)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onStepClick) onStepClick(stepId); }}
      >
        {numerate(label, `${keyPrefix}-${j}`)}
      </span>
    );
  });
}

export function renderChadMarkdown(text, opts = {}) {
  const parts = String(text ?? '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => (
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? (
        <strong key={i} className="font-semibold">
          {renderStepTokens(part.slice(2, -2), opts, i)}
        </strong>
      )
      : <React.Fragment key={i}>{renderStepTokens(part, opts, i)}</React.Fragment>
  ));
}
